import { useMemo } from "react";

import { useUser } from "../lib/rbac/useUser.ts";
import { useSqeLabs } from "./use-sqe-labs.ts";

export const LOGGED_IN_EMPLOYEE_TYPE = {
  REGULAR: "REGULAR",
  SQELABS: "SQELABS",
} as const;

export const LOGGED_IN_EMPLOYEE_LOCATION = {
  INDIA: "CCI_INDIA",
  US: "CCI_US",
} as const;

export type LoggedInEmployeeType =
  (typeof LOGGED_IN_EMPLOYEE_TYPE)[keyof typeof LOGGED_IN_EMPLOYEE_TYPE];

export type LoggedInEmployeeLocation =
  (typeof LOGGED_IN_EMPLOYEE_LOCATION)[keyof typeof LOGGED_IN_EMPLOYEE_LOCATION];

function normalizeEmployeeValue(value: unknown): string {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

export function isEmployeeType(
  value: unknown,
  expected: LoggedInEmployeeType,
): boolean {
  return normalizeEmployeeValue(value) === expected;
}

export function isEmployeeLocation(
  value: unknown,
  expected: LoggedInEmployeeLocation,
): boolean {
  return normalizeEmployeeValue(value) === expected;
}

export function isRegularEmployeeType(value: unknown): boolean {
  return isEmployeeType(value, LOGGED_IN_EMPLOYEE_TYPE.REGULAR);
}

export function isSqeLabsEmployeeType(value: unknown): boolean {
  return isEmployeeType(value, LOGGED_IN_EMPLOYEE_TYPE.SQELABS);
}

export function isIndiaEmployeeLocation(value: unknown): boolean {
  return isEmployeeLocation(value, LOGGED_IN_EMPLOYEE_LOCATION.INDIA);
}

export function isUsEmployeeLocation(value: unknown): boolean {
  return isEmployeeLocation(value, LOGGED_IN_EMPLOYEE_LOCATION.US);
}

export function useLoggedInEmployee() {
  const user = useUser();
  const isSqeLabs = useSqeLabs();

  return useMemo(
    () => ({
      user,
      employeeType: normalizeEmployeeValue(user?.empType),
      location: normalizeEmployeeValue(user?.location),
      isRegular: isRegularEmployeeType(user?.empType),
      isSqeLabs,
      isIndia: isIndiaEmployeeLocation(user?.location),
      isUs: isUsEmployeeLocation(user?.location),
    }),
    [user, isSqeLabs],
  );
}
